import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  UserPlus, 
  UserMinus, 
  UserCheck, 
  Search,
  LogOut,
  Menu,
  X,
  Clock
} from 'lucide-react';
import UserSearch from './UserSearch';
import RoleManagement from './RoleManagement';
import RequestManagement from './RequestManagement';
import { User as UserType, ApprovalRequest } from '../types/User';
import localAuthService from '../services/LocalAuthService';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'add' | 'remove' | 'change' | 'requests'>('add');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [userName, setUserName] = useState<string>('User');

  useEffect(() => {
    const loadUserInfo = () => {
      const name = localAuthService.getUserDisplayName();
      setUserName(name);
    };
    loadUserInfo();
  }, []);

  const tabs = [
    { id: 'add', label: 'Add Role', icon: UserPlus, color: 'text-green-600' },
    { id: 'remove', label: 'Remove Role', icon: UserMinus, color: 'text-red-600' },
    { id: 'change', label: 'Change Role', icon: UserCheck, color: 'text-blue-600' },
    { id: 'requests', label: 'Open Requests', icon: Clock, color: 'text-orange-600' },
  ];

  const handleUserSelect = (user: UserType) => {
    setSelectedUser(user);
  };

  const handleResetUser = () => {
    setSelectedUser(null);
  };

  const handleTabChange = (tab: string) => {
    if (tab === 'remove') {
      navigate('/roles/remove');
      return;
    }
    setActiveTab(tab as any);
    // Don't reset user when changing tabs - let them keep their selection
  };

  const handleRequestCreated = (request: ApprovalRequest) => {
    setRequests(prev => [...prev, request]);
  };

  const handleRequestUpdate = (updatedRequest: ApprovalRequest) => {
    setRequests(prev => prev.map(req => req.id === updatedRequest.id ? updatedRequest : req));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div className="flex items-center ml-4 md:ml-0">
                <Shield className="h-8 w-8 text-primary-600" />
                <h1 className="ml-2 text-xl font-bold text-gray-900">NYX+</h1>
                <span className="ml-2 text-sm text-gray-500">Access Management Portal</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:block text-sm text-gray-700">
                Welcome back, {userName}
              </div>
              <button 
                onClick={() => {
                  localAuthService.logout();
                  navigate('/login');
                }}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <LogOut size={16} className="mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block md:w-64 bg-white shadow-sm border-r border-gray-200 min-h-screen`}>
          <nav className="mt-8 px-4">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon size={20} className={`mr-3 ${tab.color}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {tabs.find(tab => tab.id === activeTab)?.label}
              </h2>
              <p className="text-gray-600">
                Manage user access and roles across your organization
              </p>
            </div>

            {/* Requests Management Section */}
            {activeTab === 'requests' && (
              <div className="mb-8">
                <RequestManagement 
                  requests={requests}
                  onRequestUpdate={handleRequestUpdate}
                />
              </div>
            )}

            {/* User Search Section */}
            {activeTab !== 'requests' && (
              <div className="mb-8">
                <UserSearch 
                  onUserSelect={handleUserSelect} 
                  selectedUser={selectedUser}
                  onReset={handleResetUser}
                />
              </div>
            )}

            {/* Role Management Section */}
            {selectedUser && activeTab !== 'requests' && (
              <div className="mb-8">
                <RoleManagement 
                  user={selectedUser} 
                  action={activeTab}
                  onUserUpdate={setSelectedUser}
                  onRequestCreated={handleRequestCreated}
                />
              </div>
            )}

            {/* Instructions */}
            {!selectedUser && activeTab !== 'requests' && (
              <div className="card">
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Search for a user to get started
                  </h3>
                  <p className="text-gray-600">
                    Enter an email address or SAP ID above to view and manage user roles
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
